<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <h5 id="invoice-detail-title" class="mb-0"><i class="fa fa-fw fa-ticket mr-2" />Detail Invoice</h5>
            <hr />
            <b-row>
                <b-col id="invoice-detail-left" md="6">
                    No. Invoice <strong>{{ data.no_faktur }}</strong><br />
                    No. Ref <strong>{{ data.no_ref }}</strong><br />
                    Divisi: <strong>{{ data.division }}</strong><br />
                    Invoice untuk <strong>{{ data.customer_name }}</strong><br />
                    Invoice dari <strong>{{ data.warehouse }} - {{ data.office }}</strong><br />
                    <template v-if="data.is_pei_source != null">
                        Penjualan PEI, Sumber: <strong>{{ data.is_pei_source ? "PEI" : "Non-PEI" }}</strong><br />
                    </template>
                    Status Invoice: <strong>{{ data.status == 0 ? "Void" : data.status == 2 ? "Ongoing" : data.status == 3 ? "Lunas" : data.status == 4 ? "Request Void" : "Error" }}</strong><br />
                    Ditransaksikan oleh <strong>{{ data.confirmed_by }}</strong> pada {{ $helper.parseDate(data.confirmed_at) }}, {{ $helper.parseHourMinute(data.confirmed_at) }}<br />
                    Tanggal Penjualan: <strong>{{ $helper.parseDate(data.sold_at) }}</strong><br />
                    Tipe Invoice: <strong>{{ invoiceTypeLabel }}</strong> <br />
                    Catatan Invoice: {{ data.note || "-" }}
                    <br />PPH: <strong>{{ data.pph ? (data.pph.direction === 'minus' ? '-' : '+') + data.pph.percentage + '% / ' + (data.pph.direction === 'minus' ? '-' : '+') + 'Rp. ' + $helper.formatThousand(data.pph.amount) : '-' }}</strong>
                    <br />Materai: <strong>{{ data.materai ? (data.materai.direction === 'minus' ? '-' : '+') + 'Rp. ' + $helper.formatThousand(data.materai.amount) : '-' }}</strong>
                </b-col>
                <b-col id="invoice-detail-right" md="6" class="text-right" v-if="data.status != 4 && data.status != 0">
                    <b-button id="reprint-invoice-button" variant="dark" @click="confirmReprintInvoice">
                        <i class="fa fa-print mr-2" />Cetak Invoice
                    </b-button>
                    <div id="payment-buttons" class="my-2">
                        <b-button id="view-payments-button" variant="primary" @click="showPayments">
                            <i class="fa fa-dollar mr-2" />Lihat Pembayaran
                        </b-button><br>
                        <b-button v-if="data.status == 2" id="edit-invoice-button" variant="primary" class="mt-3" @click="promptEditInvoice">
                            <i class="fa fa-pencil mr-2" />Ubah Detail Invoice
                        </b-button>
                    </div>
                    <div class="my-2">
                        <b-button id="change-customer-button" variant="primary" @click="promptEditCustomer" v-if="isAllowedToChangeCustomer">
                            <i class="fa fa-pencil mr-2" />Ubah Customer
                        </b-button>
                    </div>
                    <b-button id="void-invoice-button" class="px-2" variant="danger" @click="showRequestVoidInvoice" v-if="canRequestVoidInvoice">
                        <i class="fa fa-times mr-1" /> Void Invoice
                    </b-button>
                </b-col>
                <b-col id="invoice-detail-right" md="6" class="text-right" v-if="data.status == 0">
                    <b-button id="copy-invoice-button" class="px-2" variant="primary" @click="showCopyInvoice" v-if="canCopyInvoice">
                        <i class="fa fa-copy mr-1" /> Copy Invoice
                    </b-button>
                </b-col>
            </b-row>
            <hr />
            <div id="invoice-summary" class="brand-card-body" style="padding: 0">
                <div id="total-item">
                    <div class="text-value">{{ (data.total_net_weight == 0) ? "-" : data.total_net_weight }} gram</div>
                    <div class="text-uppercase text-muted small">Total Berat Bersih</div>
                </div>
                <div id="total-item">
                    <div class="text-value">{{ data.total_item }} ({{ data.total_return }})</div>
                    <div class="text-uppercase text-muted small">Total Item (Total Retur)</div>
                </div>
                <div id="subtotal">
                    <div class="text-value">Rp. {{ $helper.formatThousand(data.subtotal) }}</div>
                    <div class="text-uppercase text-muted small">Subtotal</div>
                </div>
                <div id="total">
                    <div class="text-value">Rp. {{ $helper.formatThousand(data.total) }}</div>
                    <div class="text-uppercase text-muted small">Total</div>
                    <div class="text text-muted small">DISKON KADAR : <strong>{{ data.discount_purity }}</strong></div>
                </div>
                <div id="outstanding">
                    <div class="text-value">Rp. {{ $helper.formatThousand(data.outstanding) }}</div>
                    <div class="text-uppercase text-muted small">Sisa Pembayaran</div>
                </div>
            </div>
            <hr />
            <b-row id="search-row" class="mt-3">
                <b-col id="search-col" md="4">
                    <b-form-group id="search-form-group" label="Pencarian No. Faktur / IMEI SN" description="Ketik RETUR untuk melihat barang retur">
                        <b-form-input id="search-input" v-model="filterInput" maxlength="50" />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-table id="invoice-items-table" bordered hover show-empty no-local-sorting
                :items="data.items"
                :fields="fields"
                :filter="filterInput"
                :filter-included-fields="['no_faktur_pgi', 'imei_sn', 'is_returned']"
                responsive
                @sort-changed="onSortChanged"
            >
                <template v-slot:empty>
                    Invoice kosong.
                </template>
                <template v-slot:cell(selection)="row">
                    <div v-if="row.item.is_returned == null && row.item.return_requested == false && data.status != 4 && data.status != 0">
                        <b-button :id="'return-button-' + row.index" :variant="row.item.selectedForReturn ? 'primary' : 'dark'" size="sm" @click="processReturn(row.item)">
                            <i :class="row.item.selectedForReturn ? 'fa fa-check' : 'fa fa-minus'" />
                        </b-button>
                    </div>
                </template>
                <template v-slot:cell(barang)="row">
                    {{ row.item.type }} <span v-if="row.item.year != null">({{ row.item.year }})</span>
                    <div class="small text-muted">
                        {{ row.item.kind }} - {{ row.item.brand }}<br />
                        Berat Bersih: {{ (row.item.net_weight == 0) ? "-" : row.item.net_weight }} gram<br />
                        Ctt: {{ row.item.note || "-" }}
                    </div>
                </template>
                <template v-slot:cell(noFakturImei)="row">
                    {{ row.item.no_faktur_pgi }}
                    <div class="small text-muted">
                        {{ row.item.imei_sn }}<br />
                        Grade <strong>{{ row.item.grade }} - {{ row.item.spec }} - {{ row.item.is_batangan ? 'BATANG' : 'LENGKAP' }}</strong><br />
                        Kloter {{ $helper.parseDate(row.item.created) }}
                    </div>
                </template>
                <template v-slot:cell(price)="row">
                    Rp. {{ $helper.formatThousand(row.item.base_price) }}
                    <div class="small text-muted">
                        Adj. Rp. {{ $helper.formatThousand(row.item.adjustment) }}<br />
                        Diskon Rp. {{ $helper.formatThousand(row.item.discount) }}
                    </div>
                </template>
                <template v-slot:cell(subtotal)="row">
                    Rp. {{ $helper.formatThousand(row.item.total) }}<br />
                    <b-badge v-if="row.item.is_returned == 'RETUR'" variant="warning">RETUR</b-badge>
                    <b-badge v-else-if="row.item.return_requested == true" variant="warning">RETUR REQUESTED</b-badge>
                </template>
                <template v-slot:cell(action)="row">
                    <b-button :id="'adjustment-button-' + row.index" size="sm" variant="dark" v-b-tooltip.hover.top title="Adjustment" @click="showAdjustments(row.item)">
                        <i class="fa fa-fw fa-list" />
                    </b-button>
                    <b-button v-if="row.item.is_returned == 'RETUR'" :id="'retur-detail-button-' + row.index" size="sm" class="ml-2" variant="warning" v-b-tooltip.hover.top title="Data Retur" @click="showReturDetail(row.item)">
                        <i class="fa fa-fw fa-info" />
                    </b-button>
                </template>
            </b-table>
            <div id="return-items-button-container" class="mt-4 text-right" v-if="data.status != 4 && data.status != 0">
                <b-button id="return-items-button" variant="danger" @click="confirmReturnItems">
                    Retur Barang Terpilih
                </b-button>
            </div>
        </b-card>
        <b-modal id="m-adjustment" title="Daftar Adjustment" centered hide-footer>
            Adjustment:
            <ul v-if="activeItem.adjustments.length > 0">
                <li v-for="(item, index) in activeItem.adjustments" v-bind:key="index">
                    {{ item.name }}: Rp. {{ $helper.formatThousand(item.adjustment) }}
                </li>
            </ul>
            <ul v-else>
                <li>Barang ini tidak memiliki adjustment</li>
            </ul>
        </b-modal>
        <b-modal id="m-retur-input" title="Input Retur Barang" centered hide-footer no-close-on-backdrop no-close-on-esc>
            Melakukan retur barang untuk {{ activeItem.type }} (NF {{ activeItem.no_faktur_pgi }})<br />
            <b-form-group id="retur-reason-form-group" class="mt-3" label="Alasan Retur">
            <b-input id="retur-reason-input" v-model="input.reason" maxlength="150" />
            </b-form-group>
            <div id="retur-submit-button-container" class="text-right">
            <b-button id="retur-submit-button" variant="primary" :disabled="isSubmitting" @click="addToReturn">
                {{ isSubmitting ? "Memproses..." : "Tambahkan ke Retur" }}
            </b-button>
            </div>
        </b-modal>
        <b-modal id="m-retur" title="Retur Barang" centered hide-footer no-close-on-backdrop no-close-on-esc>
            Melakukan retur barang untuk:<br />
            <ul id="retur-items-list">
            <li v-for="item in itemsToReturn" v-bind:key="item.id">
                NF <b>{{ item.noFaktur }}</b> ({{ item.type }})<br />
                Ket: {{ item.returnNote }}
            </li>
            </ul>
            <div id="retur-submit-container" class="text-right">
            <b-button id="retur-submit-button" variant="primary" :disabled="isSubmitting" @click="execReturn">
                {{ isSubmitting ? "Memproses..." : "Retur Barang" }}
            </b-button>
            </div>
        </b-modal>
        <b-modal id="m-retur-detail" title="Detail Retur" centered hide-footer>
            Retur dilakukan oleh <strong>{{ activeItem.returned_data.by }}</strong> pada {{ $helper.parseDate(activeItem.returned_data.at) }}, {{ $helper.parseHourMinute(activeItem.returned_data.at) }}
            <div class="mt-4">
                <strong>Alasan Retur</strong><br />
                {{ activeItem.returned_data.reason }}
            </div>
        </b-modal>
        <b-modal id="m-retur-success" title="Retur Barang Berhasil" centered hide-footer no-close-on-backdrop no-close-on-esc hide-header-close>
            Retur berhasil disimpan, dengan perincian pembayaran sebagai berikut:
            <ul id="retur-success-invoices-list" class="mt-2">
            <li v-for="item in returResult.invoices" v-bind:key="item.no_faktur">
                {{ item.no_faktur }} - <strong>Rp. {{ $helper.formatThousand(item.amount) }} <span v-if="item.is_settled == true">[LUNAS]</span></strong>
            </li>
            <li v-if="returResult.debt != 0">
                Sebagai hutang ke customer / deposit - <strong>Rp. {{ $helper.formatThousand(returResult.debt) }}</strong>
            </li>
            </ul>
            <div id="retur-success-print-instructions" class="mt-4">
            Tekan bagian "Sinar Mutiara Cell" untuk mencetak faktur retur.
            <iframe id="retur-success-print-iframe" :src="$BASE_PRINT_FILE_PATH + getReturnInvoiceLink()" height="300px" width="100%" />
            </div>
            <div id="retur-success-buttons" class="mt-4">
            <b-row>
                <b-col md="6">
                <b-button id="retur-success-toggle-size-button" variant="dark" :disabled="returnInvoiceSizeCooldown" @click="toggleReturnInvoiceSize">
                    Print {{ returnInvoiceSize == 'A5' ? 'A4' : 'A5' }}
                </b-button>
                </b-col>
                <b-col md="6" class="text-right">
                <b-button id="retur-success-ok-button" variant="primary" @click="$bvModal.hide('m-retur-success')">
                    OK
                </b-button>
                </b-col>
            </b-row>
            </div>
        </b-modal>
        <b-modal id="m-retur-request-success" title="Request Retur Barang Berhasil" centered hide-footer no-close-on-backdrop no-close-on-esc hide-header-close>
            <div id="retur-request-success-message">
            Request untuk retur berhasil disimpan, silahkan hubungi admin untuk proses approval.
            </div>
            <div id="retur-request-success-buttons" class="mt-4 text-right">
            <b-button id="retur-request-success-ok-button" variant="primary" @click="$bvModal.hide('m-retur-request-success')">
                OK
            </b-button>
            </div>
        </b-modal>
        <b-modal id="m-payments" title="Pembayaran Invoice" size="lg" no-close-on-backdrop no-close-on-esc hide-footer>
            <ModalInvoicePayments :invoiceId="$route.params.id" />
        </b-modal>
        <b-modal id="m-reprint-invoice" title="Cetak Ulang Invoice" centered hide-footer>
            <div id="reprint-invoice-instructions">
            Tekan bagian "Sinar Mutiara Cell" untuk mencetak.
            </div>
            <iframe id="reprint-invoice-iframe" :src="getInvoiceLink()" height="300px" width="100%" />
            <div id="reprint-invoice-buttons" class="mt-4">
            <b-row>
                <b-col md="6">
                <b-button id="reprint-invoice-toggle-size-button" variant="dark" :disabled="invoiceSizeCooldown" @click="toggleInvoiceSize">
                    Print {{ invoiceSize == 'A5' ? 'A4' : 'A5' }}
                </b-button>
                </b-col>
                <b-col md="6" class="text-right">
                <b-button id="reprint-invoice-ok-button" variant="primary" @click="$bvModal.hide('m-reprint-invoice')">
                    OK
                </b-button>
                </b-col>
            </b-row>
            </div>
        </b-modal>
        <b-modal id="m-pin" :title="pinTitle" centered hide-footer>
            <b-form-group id="pin-form-group" label="Masukan PIN">
            <b-input id="pin-input" type="password" v-model="pin" maxlength="10" @keydown.enter="checkPIN()" autofocus />
            </b-form-group>
            <div id="pin-submit-button-container" class="mt-4 text-right">
            <b-button id="pin-submit-button" variant="primary" :disabled="isSubmitting" @click="checkPIN()">
                Otorisasi
            </b-button>
            </div>
        </b-modal>
        <b-modal id="m-edit" title="Ubah Invoice" centered hide-footer>
            <b-form-group id="edit-no-ref-form-group" label="No. Ref Invoice" description="Maks. 20 karakter.">
            <b-input id="edit-no-ref-input" v-model="edit.noRef" maxlength="20" />
            </b-form-group>
            <b-form-group id="edit-note-form-group" label="Catatan Invoice">
            <b-input id="edit-note-input" v-model="edit.note" maxlength="200" />
            </b-form-group>
            <div id="edit-submit-button-container" class="mt-4 text-right">
            <b-button id="edit-submit-button" variant="primary" :disabled="isSubmitting || (edit.noRef == null && edit.note == null)" @click="confirmEditInvoice">
                {{ isSubmitting ? "Menyimpan..." : "Simpan" }}
            </b-button>
            </div>
        </b-modal>
        <b-modal id="m-edit-customer" title="Ubah Customer" centered hide-footer>
            <b-form-group id="edit-customer-group" label="Invoice ditujukan untuk...">
                <div id="edit-customer-select">
                    <multiselect v-model="edit.customer"
                        label="name"
                        track-by="id"
                        :internal-search="true"
                        deselect-label=""
                        select-label=""
                        :options="edit.optCustomer"
                    />
                </div>
            </b-form-group>
            <div class="mt-4 text-right">
            <b-button id="save-edit-customer-button" variant="primary" :disabled="isSubmitting || edit.customer == null" @click="confirmEditCustomer">
                {{ isSubmitting ? "Menyimpan..." : "Simpan" }}
            </b-button>
            </div>
        </b-modal>
        <b-modal id="m-void-invoice" title="Void Invoice" centered hide-footer no-close-on-backdrop no-close-on-esc>
            Apakah Anda yakin ingin mengajukan permintaan void untuk invoice ini? Tindakan ini akan mengirimkan request void invoice terkait.
            <b-form-group id="void-reason-form-group" class="mt-3" label="Alasan Void">
                <b-input id="void-reason-input" placeholder="Masukan alasan void" v-model="voidInput.reason" maxlength="150" />
            </b-form-group>
            <div id="void-submit-button-container" class="text-right">
                <b-button id="void-submit-button" variant="danger" :disabled="isSubmitting" @click="execRequestVoidInvoice">
                    {{ isSubmitting ? "Memproses..." : "Void Invoice" }}
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-copy-invoice" title="Copy Invoice" centered no-close-on-backdrop hide-footer>
            <b-form-group label="Ditugaskan kepada...">
                <multiselect v-model="copyInput.user"
                    label="name"
                    track-by="id"
                    :internal-search="true"
                    deselect-label=""
                    select-label=""
                    :options="optUsers"
                />
            </b-form-group>
            <b-form-group label="Tipe Invoice">
                <multiselect v-model="copyInput.invoiceType"
                    label="name"
                    deselect-label=""
                    select-label=""
                    :options="optInvoiceType"
                />
            </b-form-group>
            <div class="mt-4 text-right">
                <b-button variant="primary" :disabled="isSubmitting" @click="execCopyInvoice">
                    {{ isSubmitting ? "Membuat..." : "Copy Invoice" }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import ModalInvoicePayments from './ModalInvoicePayments.vue'
import EnumPermissions from "@/shared/enum-permissions"

export default {
    components: {
        ModalInvoicePayments
    },
    data() {
        return {

            initStatus: 0,
            isSubmitting: false,
            isFetching: false,

            filterInput: "",
            returResult: {},
            pin: "",
            pinTitle: "",
            afterPin: "",

            input: {
                reason: "",
                warehouse: null
            },

            voidInput: {
                reason: ""
            },

            copyInput: {
                office: null,
                division: null,
                user: null,
                invoiceType: null
            },

            optInvoiceType: [
                { value: "ORIGINAL_PRICE", name: "Harga Asli" },
                { value: "MELTED_SALE",    name: "Jual Lebur (Gabung Item)" },
                { value: "BUYBACK",        name: "Buyback" }
            ],

            data: {
                items: []
            },
            warehouseId: null,
            divisionId: null,

            fields: [
                { key: "barang", label: "Barang", sortable: true },
                { key: "noFakturImei", label: "No. Faktur & IMEI / SN", sortable: true },
                { key: "price", label: "Harga, Diskon, Adj", sortable: true },
                { key: "subtotal", label: "Subtotal", sortable: true },
                { key: "action", label: "Aksi" },
            ],

            activeItem: {
                adjustments: [],
                returned_data: {}
            },

            itemsToReturn: [],

            optWarehouse: [],
            optOffices: [],
            optDivisions: [],
            optUsers: [],

            canReturn: false,

            edit: {
                noRef: "",
                note: "",
                customer: null,
                optCustomer: []
            },

            sortBy: null,
            sortDesc: false,

            invoiceSize: 'A5',
            invoiceSizeCooldown: false,

            returnInvoiceSize: 'A5',
            returnInvoiceSizeCooldown: false

        }
    },

    watch: {

        "finalize.dp": function(newVal) {

            let cleaned = String(newVal).replace(/\./g, "")
            let res = cleaned.replace(/(?!^-)[^0-9]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            this.$nextTick(() => this.finalize.dp = res)

        },

    },

    methods: {

        init() {

            this.initStatus = 0

            this.copyInput.office = null
            this.copyInput.division = null
            this.copyInput.user = null
            this.optUsers = []

            let getData = this.getItems()

            let getWarehouses = this.$axios.get("master/warehouse/selection").then(res => {
                this.optWarehouse = res.data.data
            })

            let getCustomers = this.$axios.get("customer/selection").then(res => {
                this.edit.optCustomer = res.data.data
            }).catch(error => this.$helper.parseError(this, error))

            let getOffices = this.$axios.get("master/offices").then(res => {
                this.optOffices = res.data.data
            })

            let getDivisions = this.$axios.get("division/get-all-filtered").then(res => {
                this.optDivisions = res.data.data
            })

            Promise.all([getData, getWarehouses, getCustomers, getOffices, getDivisions]).then(() => {
                this.$axios.get("master/users?warehouse_id=" + this.warehouseId + "&division_id=" + this.divisionId).then(res => {
                        this.optUsers = res.data.data.map(it => {
                            return {
                                id: it.id,
                                name: it.fullname
                            }
                        })
                    })
                this.initStatus = 1
            })
            .catch(() => this.initStatus = -1)

        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy
            this.sortDesc = ctx.sortDesc
            this.getItems()
        },

        getItems() {

            const params = {
                sortBy: this.sortBy,
                sortDesc: this.sortDesc,
                category: "gold"
            }

            return this.$axios.get(`invoice/detail/${this.$route.params.id}`, { params }).then(res => {

                let data = res.data.data

                // Cek apabila invoice ini masih boleh melakukan retur
                this.fields.unshift({ key: "selection", label: "Retur?" })
                data.items.forEach(it => {
                    it.selectedForReturn = false
                    it.returnNote = ""
                })
                this.warehouseId = data.warehouse_id;
                this.divisionId = data.division_id;

                this.canReturn = data.can_return
                // if(data.can_return == true) {



                // }

                this.data = data

            })
            .catch(error => this.$helper.parseError(this, error))
        },

        getInvoiceLink() {
            const fullname = localStorage.getItem("fullname");
            const encodedFullname = fullname ? btoa(fullname) : '';
            if (this.invoiceSize == 'A5') {
                return `${this.$BASE_PRINT_FILE_PATH}/umgtinvcegold/${this.$route.params.id}-${this.data.timestamp}-${this.data.timestamp2}-${encodedFullname}`
            }
            else{
                return `${this.$BASE_PRINT_FILE_PATH}/umgtinva4gold/${this.$route.params.id}-${this.data.timestamp}-${this.data.timestamp2}-${encodedFullname}`
            }
        },

        toggleInvoiceSize() {
            this.invoiceSizeCooldown = true
            if(this.invoiceSize == 'A5') this.invoiceSize = 'A4'
            else this.invoiceSize = 'A5'
            setTimeout(function(){
                this.invoiceSizeCooldown = false
            }.bind(this),2000);
        },

        getReturnInvoiceLink() {
            if(this.returnInvoiceSize == 'A5'){
                return this.returResult.link
            }
            else{
                return this.returResult.linka4
            }
        },

        toggleReturnInvoiceSize() {
            this.returnInvoiceSizeCooldown = true
            if(this.returnInvoiceSize == 'A5') this.returnInvoiceSize = 'A4'
            else this.returnInvoiceSize = 'A5'
            setTimeout(function(){
                this.returnInvoiceSizeCooldown = false
            }.bind(this),2000);
        },

        showAdjustments(item) {

            this.activeItem = item
            this.$bvModal.show("m-adjustment")

        },

        showRetur(item) {
            this.activeItem = item
            this.$bvModal.show("m-retur")
        },

        showReturDetail(item) {
            this.activeItem = item
            this.$bvModal.show("m-retur-detail")
        },

        showPayments() {
            this.$bvModal.show("m-payments")
        },

        processReturn(item) {

            this.activeItem = item
            if(this.activeItem.selectedForReturn) {
                let isConfirmed = confirm("Hapus barang ini dari daftar barang yang akan diretur?")
                if(!isConfirmed) return

                item.selectedForReturn = false
                item.returnNote = ""
            }
            else {
                this.$bvModal.show("m-retur-input")
            }

        },

        addToReturn() {

            if(this.$helper.isBlank(this.input.reason)) {
                return this.$helper.toastError(this, "Mohon mengisi keterangan!")
            }

            this.activeItem.selectedForReturn = true
            this.activeItem.returnNote = this.input.reason
            this.input.reason = ""
            this.$bvModal.hide("m-retur-input")

        },

        confirmReturnItems() {

            this.itemsToReturn = []
            this.data.items.forEach(it => {
                if(it.selectedForReturn) {
                    this.itemsToReturn.push({
                        id: it.id,
                        noFaktur: it.no_faktur_pgi,
                        type: it.type,
                        returnNote: it.returnNote
                    })
                }
            })

            if(this.itemsToReturn.length == 0) return this.$helper.toastError(this, "Mohon memilih minimal 1 barang untuk diretur!")
            this.$bvModal.show("m-retur")

        },

        execReturn() {

            let isConfirmed = confirm("Lakukan retur untuk barang-barang ini?")
            if(!isConfirmed) return

            this.isSubmitting = true

            let invoiceDetails = []
            this.itemsToReturn.forEach(it => {
                invoiceDetails.push({
                    id: it.id,
                    reason: it.returnNote
                })
            })

            let payload = {
                invoice_id: this.$route.params.id,
                invoiceDetails: invoiceDetails
            }

            this.$axios.post("invoice/return-items", payload).then(res => {

                this.returResult = res.data.data
                if(this.returResult.status == "RETUR"){
                    this.$bvModal.show("m-retur-success")
                }else{
                    this.$bvModal.show("m-retur-request-success")
                }

                this.$bvModal.hide("m-retur")
                this.input.reason = ""
                this.init()

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        confirmReprintInvoice() {

            this.afterPin = "reprint-invoice"
            this.pinTitle = "Cetak Ulang Invoice"
            this.$bvModal.show("m-pin")

        },

        promptEditInvoice() {

            this.$bvModal.show("m-edit")

        },
        promptEditCustomer() {

            this.$bvModal.show("m-edit-customer")
        },

        confirmEditInvoice() {

            this.afterPin = "edit-invoice"
            this.pinTitle = "Perubahan Invoice"
            this.$bvModal.show("m-pin")

        },
        confirmEditCustomer() {
            this.afterPin = "edit-customer"
            this.pinTitle = "Ubah Customer"
            this.$bvModal.show("m-pin")
        },

        execEditInvoice() {

            this.isSubmitting = true
            let payload = {
                id: this.$route.params.id,
                noRef: this.edit.noRef,
                note: this.edit.note
            }

            this.$axios.post("invoice/update-invoice-final", payload).then(() => {
                this.$helper.toastSuccess(this, "Detail Invoice berhasil diubah!")
                this.init()
                this.$bvModal.hide("m-edit")
            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },
        execChangeCustomer() {
            this.isSubmitting = true
            let payload = {
                customer_id: this.edit.customer.id,
            }

            this.$axios.post(`invoice/change-customer-finalize/${this.$route.params.id}`, payload).then(() => {
                this.$helper.toastSuccess(this, "Customer berhasil diubah!")
                this.init()
                this.$bvModal.hide("m-edit-customer")
            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)
        },
        checkPIN() {

            if(this.pin == "") return this.$helper.toastError(this, "PIN kosong!")

            let payload = {
                pin: this.pin
            }

            this.isSubmitting = true
            this.$axios.post("invoice/check-pin", payload).then(() => {
                this.pin = ""
                this.$bvModal.hide("m-pin")
                switch(this.afterPin){
                    case 'reprint-invoice':
                        this.$bvModal.show("m-reprint-invoice")
                        break
                    case 'edit-invoice':
                        this.execEditInvoice()
                        break
                    case 'edit-customer':
                        this.execChangeCustomer()
                        break
                }
            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        showRequestVoidInvoice() {
            this.$bvModal.show("m-void-invoice")
        },

        execRequestVoidInvoice() {
            this.isSubmitting = true
            this.$axios
                .post("invoice-void/request/" + this.$route.params.id, {
                    reason: this.voidInput.reason
                })
                .then(() => {
                    this.$helper.toastSuccess(this, "Permintaan void invoice berhasil dikirim!")
                    this.$bvModal.hide("m-void-invoice")
                    this.init()
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false))
        },

        showCopyInvoice() {
            this.$bvModal.show("m-copy-invoice")
        },

        execCopyInvoice() {
            this.isSubmitting = true
            this.$axios
                .post("invoice-void/copy-gold/" + this.$route.params.id, {
                    assignee_id:  this.copyInput.user ? this.copyInput.user.id : null,
                    invoice_type: this.copyInput.invoiceType ? this.copyInput.invoiceType.value : null
                })
                .then((res) => {
                    this.$helper.toastSuccess(this, "Invoice berhasil dicopy!")

                    this.$router.push({ name: 'Draft Invoice Emas', params: { id: res.data.data.invoice_id } });
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false))
        }

    },
    computed: {
        invoiceTypeLabel() {
            const invoiceType = this.data.invoice_type;
            switch (invoiceType) {
                case 'AUCTION':
                    return 'Lelang'
                case 'ORIGINAL_PRICE':
                    return 'Harga Asli'
                case 'MELTED_SALE':
                    return 'Jual Lebur'
                case 'BUYBACK':
                    return 'Buyback'
                default:
                    return '';
            }
        },
        isBuyback() {
            return this.data.invoice_type === 'BUYBACK'
        },
        isAllowedToChangeCustomer() {
            return (
                this.$helper.checkRoleNumber(
                    EnumPermissions.CHANGE_CUSTOMER_INVOICE_FINALIZED.toString(),
                    localStorage.getItem("permissions")
                ) || localStorage.getItem("roleName").includes("Superadmin")
            )
        },
        canCopyInvoice() {
            const hasPermission =
                this.$helper.checkRoleNumber(
                    EnumPermissions.PENJUALAN_COPY_INVOICE_VOID.toString(),
                    localStorage.getItem("permissions")
                ) || localStorage.getItem("roleName").includes("Superadmin")

            return hasPermission && this.data.status === 0
        },
        canRequestVoidInvoice() {
            const hasPermission =
                this.$helper.checkRoleNumber(
                    EnumPermissions.PENJUALAN_REQUEST_INVOICE_VOID.toString(),
                    localStorage.getItem("permissions")
                ) || localStorage.getItem("roleName").includes("Superadmin")

            return hasPermission && (this.data.status === 2 || this.data.status === 3)
        }
    },
    created() {
        this.init()
    }
}
</script>

<style>

</style>
